import React, { useCallback, useId, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import './StatsCard.scss';

/**
 * StatsGrid — the standard KPI row wrapper for StatsCard.
 *
 * Defaults to a 4-up desktop grid that steps down to 3 / 2 columns. Pass
 * `cols` when a page shows a different number of cards (3, 5, 6…) so the
 * desktop row stays even; the responsive step-downs are handled in SCSS.
 *
 * ── Collapsible ─────────────────────────────────────────────────────────────
 * `collapsible` folds the cards behind a disclosure and remembers the choice per
 * browser under `vc-stats-collapsed:<storageKey>` — the same `vc-<thing>:<key>`
 * shape `useColumnResize` persists column widths with.
 *
 *   <StatsGrid collapsible storageKey="enquiries" title="Pipeline overview"
 *              actions={<DateRangePresets … />}>
 *
 * It exists because a KPI row costs ~125px, and on a page bounded by
 * `.app-page-fill` — where the table scrolls its own body rather than the page
 * scrolling — that is half the rows the table could otherwise show. Folding it
 * roughly doubles them.
 *
 * `actions` is whatever must stay reachable while the cards are folded. On
 * `/enquiries` that is the date-range presets, which drive the TABLE as well as
 * the cards, so they cannot be folded away with them.
 *
 * ⚠️ `className` lands on the element this roots: the grid when it is a plain
 * grid, the BLOCK when it is collapsible. `/students` hides the whole thing
 * below 768px through it, so its selector names `.stats-block` accordingly.
 */

const STORAGE_PREFIX = 'vc-stats-collapsed:';

const readCollapsed = (storageKey) => {
  if (!storageKey || typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(STORAGE_PREFIX + storageKey) === '1';
  } catch {
    // Storage denied (private mode, blocked cookies). A KPI row that cannot
    // remember whether it was folded is not one that should fail to render.
    return false;
  }
};

const writeCollapsed = (storageKey, collapsed) => {
  if (!storageKey || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_PREFIX + storageKey, collapsed ? '1' : '0');
  } catch { /* quota or denied — the choice still holds for this page view */ }
};

const StatsGrid = ({
  cols = 4,
  collapsible = false,
  storageKey,
  title = 'Overview',
  actions,
  className = '',
  style,
  children,
}) => {
  // Read once, in the initialiser, so a folded row never flashes open first.
  const [collapsed, setCollapsed] = useState(
    () => (collapsible ? readCollapsed(storageKey) : false)
  );
  const gridId = useId();

  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      writeCollapsed(storageKey, !prev);
      return !prev;
    });
  }, [storageKey]);

  const grid = (
    <div
      id={collapsible ? gridId : undefined}
      className={`stats-grid ${collapsible ? '' : className}`}
      style={{ '--stats-grid-cols': cols, ...style }}
      // Hidden, not unmounted: folding must not re-run a card's fetch or render,
      // nor drop it out of the reading order when it comes back.
      hidden={collapsed || undefined}
    >
      {children}
    </div>
  );

  if (!collapsible) return grid;

  return (
    <section className={`stats-block ${collapsed ? 'is-collapsed' : ''} ${className}`}>
      <div className="stats-block__head">
        {/* The whole title is the control. A chevron beside a heading that is
            not itself clickable is a target most people never find. */}
        <button
          type="button"
          className="stats-block__toggle"
          aria-expanded={!collapsed}
          aria-controls={gridId}
          onClick={toggle}
        >
          <ChevronDown size={16} className="stats-block__chevron" aria-hidden="true" />
          <span>{title}</span>
        </button>
        {actions && <div className="stats-block__actions">{actions}</div>}
      </div>
      {grid}
    </section>
  );
};

export default StatsGrid;
